import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  Chip,
  IconButton,
  TextField,
  Stack,
  Button,
  TablePagination,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import { Groups, VpnKey, Visibility, EmojiEvents } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { userService, UserListItem, UserDetails } from '../services/userService';
import { AppHeader } from '../components/AppHeader';

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedUserDetails, setSelectedUserDetails] = useState<UserDetails | null>(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  // Contador da requisição mais recente — sem isso, digitar rápido dispara
  // uma busca por letra e, se as respostas chegarem fora de ordem (rede
  // instável), a tela pode acabar mostrando o resultado de uma busca já
  // superada em vez da mais recente.
  const latestRequestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const fetchUsers = useCallback(async () => {
    const requestId = ++latestRequestIdRef.current;
    try {
      setLoading(true);
      const data = await userService.getUsers({
        search: debouncedSearch || undefined,
        page: page + 1,
        limit: rowsPerPage,
      });
      if (requestId !== latestRequestIdRef.current) return;
      setUsers(data.users);
      setTotal(data.total);
    } catch (error) {
      if (requestId !== latestRequestIdRef.current) return;
      console.error('Erro ao carregar usuários:', error);
      setUsers([]);
      setTotal(0);
    } finally {
      if (requestId === latestRequestIdRef.current) setLoading(false);
    }
  }, [debouncedSearch, page, rowsPerPage]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleViewDetails = async (userId: string) => {
    setShowDetailsDialog(true);
    setLoadingDetails(true);
    setSelectedUserDetails(null);
    try {
      const details = await userService.getUserDetails(userId);
      setSelectedUserDetails(details);
    } catch (error) {
      console.error('Erro ao carregar detalhes do usuário:', error);
    } finally {
      setLoadingDetails(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value);
    setPage(0);
  };

  const handleResetPassword = (cpf: string) => {
    navigate(`/reset-user-password?cpf=${encodeURIComponent(cpf)}`);
  };

  return (
    <>
      <AppHeader
        title="Usuários"
        subtitle="Consulte os usuários finais cadastrados na plataforma"
        icon={<Groups />}
      />

      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: 'primary.main' }}>
              Usuários
            </Typography>
            {!loading && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {search
                  ? `${total} usuário(s) encontrado(s) para "${search}"`
                  : `${total} usuário(s) cadastrado(s) no total`}
              </Typography>
            )}
          </Box>
        </Box>

        <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              fullWidth
              label="Buscar por nome, CPF ou email"
              value={search}
              onChange={handleSearchChange}
              size="small"
              placeholder="Ex: Maria, 123.456.789-00, maria@email.com"
            />
            {search && (
              <Button
                variant="outlined"
                onClick={() => {
                  setSearch('');
                  setPage(0);
                }}
                size="small"
                sx={{ minWidth: '100px', height: 'fit-content' }}
              >
                Limpar
              </Button>
            )}
          </Stack>
        </Paper>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>CPF</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Cidade/Estado</TableCell>
                <TableCell align="center">Cupons</TableCell>
                <TableCell>Cadastrado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                      <CircularProgress size={40} />
                      <Typography variant="body2" color="text.secondary">
                        Carregando usuários...
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="h6" gutterBottom sx={{ color: 'text.secondary' }}>
                        Nenhum usuário encontrado
                      </Typography>
                      {search ? (
                        <Box>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            Não há usuários que correspondam à busca aplicada.
                          </Typography>
                          <Button
                            variant="outlined"
                            size="small"
                            onClick={() => {
                              setSearch('');
                              setPage(0);
                            }}
                          >
                            Limpar busca e ver todos os usuários
                          </Button>
                        </Box>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          Ainda não há usuários finais cadastrados.
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id}>
                    <TableCell>{u.name}</TableCell>
                    <TableCell>{u.cpf}</TableCell>
                    <TableCell>{u.email || '—'}</TableCell>
                    <TableCell>{u.phone || '—'}</TableCell>
                    <TableCell>{[u.city, u.state].filter(Boolean).join('/') || '—'}</TableCell>
                    <TableCell align="center">
                      <Chip label={u.couponCount} size="small" color={u.couponCount > 0 ? 'primary' : 'default'} />
                    </TableCell>
                    <TableCell>{new Date(u.createdAt).toLocaleDateString('pt-BR')}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Ver detalhes">
                        <IconButton size="small" onClick={() => handleViewDetails(u.id)}>
                          <Visibility fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Redefinir senha">
                        <IconButton size="small" onClick={() => handleResetPassword(u.cpf)}>
                          <VpnKey fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage="Itens por página"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
          />
        </TableContainer>
      </Container>

      <Dialog
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Detalhes do usuário</DialogTitle>
        <DialogContent>
          {loadingDetails ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={32} />
            </Box>
          ) : !selectedUserDetails ? (
            <Typography color="text.secondary">Não foi possível carregar os detalhes.</Typography>
          ) : (
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6">{selectedUserDetails.name}</Typography>
                <Typography variant="body2" color="text.secondary">
                  CPF: {selectedUserDetails.cpf} • {selectedUserDetails.email || 'sem email'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {selectedUserDetails.phone || 'sem telefone'} •{' '}
                  {[selectedUserDetails.city, selectedUserDetails.state].filter(Boolean).join('/') || 'sem localização'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Cadastrado em {new Date(selectedUserDetails.createdAt).toLocaleDateString('pt-BR')}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Cupons por campanha ({selectedUserDetails.totalCoupons} no total)
                </Typography>
                {selectedUserDetails.couponsByCampaign.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum cupom cadastrado.
                  </Typography>
                ) : (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Campanha</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="right">Cupons</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedUserDetails.couponsByCampaign.map((c) => (
                        <TableRow key={c.campaignId}>
                          <TableCell>{c.campaignName}</TableCell>
                          <TableCell>
                            <Chip
                              label={c.campaignStatus}
                              size="small"
                              color={c.campaignStatus === 'ACTIVE' ? 'success' : 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">{c.couponCount}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Sorteios ganhos
                </Typography>
                {selectedUserDetails.drawWins.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    Nenhum sorteio ganho ainda.
                  </Typography>
                ) : (
                  <Stack spacing={1}>
                    {selectedUserDetails.drawWins.map((w, i) => (
                      <Box
                        key={`${w.drawId}-${i}`}
                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                      >
                        <EmojiEvents fontSize="small" color="warning" />
                        <Typography variant="body2">
                          {w.prizeName} — {w.campaignName} ({new Date(w.winningDate).toLocaleDateString('pt-BR')})
                        </Typography>
                        <Chip label={w.status} size="small" />
                      </Box>
                    ))}
                  </Stack>
                )}
              </Box>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserList;
